import { Link } from "react-router-dom";
import { useState } from "react";
import { ArrowRight, Shield, Sparkles, Layers, Boxes, Settings2, Zap, BarChart3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { TID } from "@/lib/testIds";
import { useNavigate } from "react-router-dom";
import ThemeToggle from "@/components/ThemeToggle";

const PILLARS = [
  { icon: Layers, t: "Várias empresas, uma só plataforma", d: "Cada empresa tem seus próprios dados, usuários e identidade — totalmente separados." },
  { icon: Boxes, t: "Modelos prontos por segmento", d: "Beleza, Saúde, Pet, Educação e Assistência — comece com tudo já pré-configurado." },
  { icon: Settings2, t: "Marca personalizada", d: "Logo, cores, favicon e tela de login com a cara de cada empresa." },
  { icon: Zap, t: "Personalização total", d: "Ative recursos, crie campos e fluxos do jeito da sua empresa, sem afetar ninguém." },
];

const MODULES = [
  "Painel", "Clientes", "Funil de Vendas", "Cadastro de Clientes", "Agenda", "Serviços",
  "Vendas", "Pacotes", "Assinaturas", "Financeiro", "Campanhas", "WhatsApp",
  "Automações", "Pesquisa de Satisfação", "Fidelidade", "Clube de Benefícios",
  "Indicação Premiada", "Relatórios", "Configurações",
];

export default function Landing() {
  const [slug, setSlug] = useState("");
  const nav = useNavigate();

  const goTenant = (e) => {
    e.preventDefault();
    const v = slug.trim().toLowerCase().replace(/[^a-z0-9-]/g, "");
    if (v) nav(`/t/${v}/login`);
  };

  return (
    <div className="min-h-screen bg-background text-foreground relative overflow-hidden">
      <div className="absolute inset-0 swiss-grid opacity-30 pointer-events-none" />
      <div className="absolute inset-0 grain-bg pointer-events-none" />

      {/* Header */}
      <header className="relative z-10 flex items-center justify-between px-6 md:px-10 py-6">
        <div className="flex items-center gap-2.5">
          <img src="/sua-plataforma-logo.png" alt="SUA PLATAFORMA" className="w-9 h-9 rounded-lg bg-secondary p-1 object-contain shrink-0" />
          <div>
            <div className="font-display font-bold tracking-tight text-lg leading-none">SUA PLATAFORMA</div>
            <div className="text-xs text-muted-foreground tracking-wider uppercase">Crescimento · Relacionamento · Fidelidade</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <ThemeToggle testId="landing-theme-toggle" />
          <Link
            to="/master/login"
            data-testid={TID.landingGoMaster}
            className="inline-flex items-center gap-1.5 text-sm font-medium px-4 py-2 rounded-full border border-border hover:bg-muted transition-colors"
          >
            <Shield className="w-4 h-4" /> Painel Master
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative z-10 px-6 md:px-10 pt-10 md:pt-20 pb-20 max-w-6xl">
        <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground mb-6">
          <Sparkles className="w-3.5 h-3.5" /> Crescimento, Relacionamento e Fidelização para sua empresa
        </div>
        <h1 className="font-display font-bold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[0.95] max-w-3xl">
          Uma plataforma. <span className="text-muted-foreground">Milhares de marcas.</span><br/>
          Um motor de crescimento.
        </h1>
        <p className="mt-6 text-base md:text-lg text-muted-foreground max-w-2xl leading-relaxed">
          Plataforma com a marca da sua empresa para Beleza, Saúde, Pet, Educação e Assistência.
          Atrair clientes, fechar vendas, fidelizar e gerar receita recorrente — tudo num só lugar.
        </p>

        <form onSubmit={goTenant} className="mt-10 flex flex-col sm:flex-row gap-3 max-w-xl">
          <Input
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder="endereco-da-sua-empresa"
            className="h-12 text-base bg-card"
            data-testid={TID.landingTenantSlug}
          />
          <Button type="submit" data-testid={TID.landingGoTenant} className="h-12 px-6 rounded-md gap-2 font-medium">
            Entrar como empresa <ArrowRight className="w-4 h-4" />
          </Button>
        </form>
        <p className="mt-2 text-xs text-muted-foreground">
          Exemplo: crie sua empresa no Painel Master e use o endereço definido (ex.: <span className="kbd">acme-beleza</span>).
        </p>
      </section>

      {/* 4-layer architecture */}
      <section className="relative z-10 px-6 md:px-10 pb-20 max-w-6xl">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {PILLARS.map((p, i) => (
            <Card key={p.t} className="p-6 rounded-xl border bg-card animate-fade-up" style={{ animationDelay: `${i * 80}ms` }}>
              <p.icon className="w-5 h-5 mb-4" strokeWidth={1.5} />
              <div className="font-display font-semibold text-lg mb-1">{p.t}</div>
              <div className="text-sm text-muted-foreground leading-relaxed">{p.d}</div>
            </Card>
          ))}
        </div>
      </section>

      {/* Modules grid */}
      <section className="relative z-10 px-6 md:px-10 pb-24 max-w-6xl">
        <div className="flex items-baseline gap-3 mb-6">
          <BarChart3 className="w-4 h-4" />
          <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">19 recursos que você pode ativar para cada empresa</h2>
        </div>
        <div className="flex flex-wrap gap-2">
          {MODULES.map((m) => (
            <span key={m} className="px-3 py-1.5 rounded-full border border-border text-sm bg-card hover:bg-muted transition-colors">
              {m}
            </span>
          ))}
        </div>
      </section>

      <footer className="relative z-10 border-t border-border px-6 md:px-10 py-6 text-xs text-muted-foreground flex items-center justify-between">
        <div>© SUA PLATAFORMA — Crescimento, Relacionamento e Fidelização</div>
        <div className="hidden sm:block">Várias empresas · Marca personalizada · Modelos prontos</div>
      </footer>
    </div>
  );
}
